// Dashboard.js - Startsida: "min dag" för behandlaren
//
// Visar inloggad användares dag: dagens bokningar, att göra (osignerade
// anteckningar), kommande grupper, senast öppnade patienter och patientsök.
// Dagskassa visas endast för admin. Allt gate:as på use_internal_journal -
// kliniker utan journalmodulen får en enkel välkomstvy.

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../UserContext';
import { isClinicLevelAdmin } from '../../utils/permissions';
import { getRecentPatients } from '../../utils/recentPatients';
import { getAppointments, getPatients, appointmentKey } from '../Calendar/calendarApi';
import apiClient from '../../apiClient';
import QuickPatientModal from '../Sidebar/QuickPatientModal';
import ConfirmDialog from '../common/ConfirmDialog';
import { uteblivetBekraftelseText } from '../Calendar/uteblivetApi';
import BoardCard from './BoardCard';
import './Dashboard.css';

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatTime = (isoString) => {
  const d = new Date(isoString);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const greetingForHour = (hour) => {
  if (hour < 12) return 'God morgon';
  if (hour < 18) return 'God eftermiddag';
  return 'God kväll';
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const useInternalJournal = Boolean(user?.clinic?.use_internal_journal);
  const isAdmin = isClinicLevelAdmin(user);

  // Högerklicksmeny på bokningsrader: öppna journal / starta inspelning direkt
  const [rowMenu, setRowMenu] = useState(null); // {x, y, appointment}

  // Åtgärdsmeny på "Att åtgärda"-poster + snabbkassa-modal
  const [actionMenu, setActionMenu] = useState(null); // {x, y, item}
  const [cashModal, setCashModal] = useState(null); // item som kassaregistreras
  const [cashAmount, setCashAmount] = useState('');
  const [cashMethod, setCashMethod] = useState('cash');
  const [savingCash, setSavingCash] = useState(false);

  // Bekräftelse för "Ta bort påminnelse" (Spår R) — appkonsekvent dialog i
  // stället för webbläsarens window.confirm.
  const [dismissTarget, setDismissTarget] = useState(null); // åtgärdspost som avfärdas
  const [dismissing, setDismissing] = useState(false);

  // "Koppla notat" (Spår A): länka ett befintligt notat (skrivet utan
  // bokningskontext) till en flaggad bokning så påminnelsen försvinner.
  const [linkModal, setLinkModal] = useState(null); // { item }
  const [linkEntries, setLinkEntries] = useState([]);
  const [linkEntriesLoading, setLinkEntriesLoading] = useState(false);
  const [linkingEntryId, setLinkingEntryId] = useState(null);

  useEffect(() => {
    if (!rowMenu) return;
    const close = () => setRowMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close, true);
    };
  }, [rowMenu]);

  useEffect(() => {
    if (!actionMenu) return;
    const close = () => setActionMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [actionMenu]);

  const handleRowContextMenu = (e, a) => {
    // Bara enskilda patientbesök (inte gruppbokningar)
    if (a.is_group || !a.patient_id) return;
    e.preventDefault();
    e.stopPropagation();
    setRowMenu({ x: e.clientX, y: e.clientY, appointment: a });
  };

  // Dagens + veckans bokningar (mina)
  const [appointments, setAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(true);

  // Osignerade anteckningar (mina)
  const [unsigned, setUnsigned] = useState({ entries: [], total: 0 });

  // Att åtgärda: betalda utan anteckning + bortglömda bokningar (mina)
  const [actionItems, setActionItems] = useState({
    items: [],
    total: 0,
    counts: { paid_no_note: 0, forgotten: 0 }
  });

  // Oknutna betalningar (lösa betalningar utan bokning) - kan knytas härifrån
  const [unlinkedPayments, setUnlinkedPayments] = useState({ items: [], total: 0 });
  const [linkChoice, setLinkChoice] = useState({}); // { [payment_id]: appointment_id }
  const [linkingId, setLinkingId] = useState(null);

  // Dagskassa (endast admin)
  const [cashTotal, setCashTotal] = useState(null);

  // Senast öppnade patienter (localStorage)
  const [recentPatients, setRecentPatients] = useState([]);

  // Patientsök
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimer = useRef(null);

  // Ny patient-modal
  const [showQuickPatient, setShowQuickPatient] = useState(false);

  const now = new Date();
  const todayStr = formatLocalDate(now);

  // Ladda data
  useEffect(() => {
    if (!useInternalJournal || !user?.user_id) return;

    const weekAhead = new Date();
    weekAhead.setDate(weekAhead.getDate() + 8);

    const loadAppointments = async () => {
      try {
        const data = await getAppointments(todayStr, formatLocalDate(weekAhead), user.user_id);
        setAppointments(data.filter((a) => a.status !== 'cancelled'));
      } catch (err) {
        console.error('Kunde inte ladda bokningar:', err);
      } finally {
        setAppointmentsLoading(false);
      }
    };

    const loadUnsigned = async () => {
      try {
        const resp = await apiClient.get('/api/journal/entries/unsigned', { params: { limit: 5 } });
        setUnsigned(resp.data);
      } catch (err) {
        // Modul avstängd eller fel - visa inget
      }
    };

    const loadCash = async () => {
      if (!isAdmin) return;
      try {
        const resp = await apiClient.get('/api/calendar/daily-cash-report', { params: { date: todayStr } });
        setCashTotal(resp.data.total);
      } catch (err) {
        // Visa inget vid fel
      }
    };

    const loadActionItems = async () => {
      try {
        const resp = await apiClient.get('/api/journal/action-items', { params: { days: 90, limit: 20 } });
        setActionItems(resp.data);
      } catch (err) {
        // Modul avstängd eller fel - visa inget
      }
    };

    const loadUnlinkedPayments = async () => {
      try {
        const resp = await apiClient.get('/api/calendar/payments/unlinked');
        setUnlinkedPayments(resp.data);
      } catch (err) {
        // Modul avstängd eller fel - visa inget
      }
    };

    loadAppointments();
    loadUnsigned();
    loadCash();
    loadActionItems();
    loadUnlinkedPayments();
    setRecentPatients(getRecentPatients(user.user_id).slice(0, 5));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useInternalJournal, user?.user_id]);

  // Debounced patientsök
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = searchTerm.trim();
    if (term.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      try {
        const data = await getPatients(term, { limit: 8 });
        setSearchResults(data.patients || data || []);
        setSearchOpen(true);
      } catch (err) {
        console.error('Patientsök misslyckades:', err);
      }
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  const openPatient = (patientId) => {
    setSearchOpen(false);
    setSearchTerm('');
    navigate(`/patients/${patientId}`);
  };

  const openAppointment = (a) => {
    if (a.is_group) {
      navigate('/calendar', {
        // Ett materialiserat tillfälle är en egen bokning; ett virtuellt bär
        // seriens id och löses upp via datumet i kalendern (Spår R)
        state: { openGroupAppointmentId: a.appointment_id, date: a.start_time }
      });
    } else if (a.patient_id) {
      navigate(`/patients/${a.patient_id}`);
    } else {
      navigate('/calendar');
    }
  };

  // Klick på en "Att åtgärda"-post öppnar en liten åtgärdsmeny vid muspekaren.
  const openActionMenu = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setActionMenu({ x: e.clientX, y: e.clientY, item });
  };

  // Skriv journal: patientens journal med förifylld ny anteckning för bokningen.
  const writeJournalForItem = (item) => {
    setActionMenu(null);
    navigate(`/patients/${item.patient_id}?tab=journal&ny_journal=1&appointment_id=${item.appointment_id}`);
  };

  // Öppna snabbkassa för en post (belopp förifyllt från bokningstypen).
  const openQuickCash = (item) => {
    setActionMenu(null);
    setCashModal(item);
    setCashAmount(item.appointment_type?.default_price ? String(item.appointment_type.default_price) : '');
    setCashMethod('cash');
  };

  const submitQuickCash = async () => {
    // Avgiftsfritt (ej fysiskt besök) är alltid 0 kr - kräver inget belopp
    const avgiftsfri = cashMethod === 'avgiftsfri';
    if (!cashModal || (!cashAmount && !avgiftsfri)) return;
    try {
      setSavingCash(true);
      await apiClient.post('/api/calendar/payments', {
        appointment_id: cashModal.appointment_id,
        amount: avgiftsfri ? 0 : parseFloat(cashAmount),
        payment_method: cashMethod,
      });
      // Bokningen är nu betald men kan sakna anteckning → visa som 'paid_no_note'.
      setActionItems((prev) => {
        const items = prev.items.map((it) =>
          it.appointment_id === cashModal.appointment_id
            ? { ...it, kind: 'paid_no_note', is_paid: true }
            : it
        );
        return {
          ...prev,
          items,
          counts: {
            paid_no_note: items.filter((i) => i.kind === 'paid_no_note').length,
            forgotten: items.filter((i) => i.kind === 'forgotten').length,
          },
        };
      });
      setCashModal(null);
    } catch (err) {
      console.error('Kunde inte registrera betalning:', err);
    } finally {
      setSavingCash(false);
    }
  };

  // Markera uteblivet (Spår D+E): uteblivet-avgift i kassan + status no_show
  // + fakturaunderlag mailas automatiskt (posten faller ur listan).
  const markNoShow = async (item) => {
    setActionMenu(null);
    if (!window.confirm(await uteblivetBekraftelseText('Markera bokningen som uteblivet?', item.patient_id, item.start_time))) return;
    try {
      const res = await apiClient.post(`/api/calendar/appointments/${item.appointment_id}/no-show`);
      if (res.data?.fakturaunderlag_sent === false) {
        window.alert(`Uteblivet registrerat, men fakturaunderlaget kunde inte mailas: ${res.data?.fakturaunderlag_error || 'okänt fel'}`);
      }
      setActionItems((prev) => {
        const items = prev.items.filter((i) => i.appointment_id !== item.appointment_id);
        return {
          items,
          total: items.length,
          counts: {
            paid_no_note: items.filter((i) => i.kind === 'paid_no_note').length,
            forgotten: items.filter((i) => i.kind === 'forgotten').length,
          },
        };
      });
    } catch (err) {
      console.error('Kunde inte markera uteblivet:', err);
      window.alert(err.response?.data?.error || 'Kunde inte markera uteblivet');
    }
  };

  // Ta bort påminnelse (Spår R): öppnar bekräftelsedialogen. Själva avfärdandet
  // sker i confirmDismiss när användaren bekräftar.
  const dismissActionItem = (item) => {
    setActionMenu(null);
    setDismissTarget(item);
  };

  // Avfärda posten permanent per (appointment_id + åtgärdstyp). Posten döljs
  // ur listan och kommer inte tillbaka efter reload (filtreras serverside).
  const confirmDismiss = async () => {
    const item = dismissTarget;
    if (!item) return;
    try {
      setDismissing(true);
      await apiClient.post('/api/journal/action-items/dismiss', {
        appointment_id: item.appointment_id,
        item_type: item.kind,
      });
      setActionItems((prev) => {
        const items = prev.items.filter((i) => i.appointment_id !== item.appointment_id);
        return {
          items,
          total: items.length,
          counts: {
            paid_no_note: items.filter((i) => i.kind === 'paid_no_note').length,
            forgotten: items.filter((i) => i.kind === 'forgotten').length,
          },
        };
      });
      setDismissTarget(null);
    } catch (err) {
      console.error('Kunde inte ta bort påminnelse:', err);
      window.alert(err.response?.data?.error || 'Kunde inte ta bort påminnelse');
    } finally {
      setDismissing(false);
    }
  };

  // Öppna "Koppla notat": hämtar patientens okopplade notat runt bokningsdagen.
  const openLinkNote = async (item) => {
    setActionMenu(null);
    setLinkModal({ item });
    setLinkEntries([]);
    setLinkEntriesLoading(true);
    try {
      const resp = await apiClient.get('/api/journal/entries/linkable', {
        params: { appointment_id: item.appointment_id },
      });
      setLinkEntries(resp.data.entries || []);
    } catch (err) {
      console.error('Kunde inte hämta notat att koppla:', err);
    } finally {
      setLinkEntriesLoading(false);
    }
  };

  // Koppla valt notat till bokningen → posten faller ur "Att åtgärda".
  const linkNoteToItem = async (entryId) => {
    if (!linkModal) return;
    const item = linkModal.item;
    try {
      setLinkingEntryId(entryId);
      await apiClient.patch(`/api/journal/entries/${entryId}/link-appointment`, {
        appointment_id: item.appointment_id,
      });
      setActionItems((prev) => {
        const items = prev.items.filter((i) => i.appointment_id !== item.appointment_id);
        return {
          items,
          total: items.length,
          counts: {
            paid_no_note: items.filter((i) => i.kind === 'paid_no_note').length,
            forgotten: items.filter((i) => i.kind === 'forgotten').length,
          },
        };
      });
      setLinkModal(null);
    } catch (err) {
      console.error('Kunde inte koppla notat:', err);
      window.alert(err.response?.data?.error || 'Kunde inte koppla notat');
    } finally {
      setLinkingEntryId(null);
    }
  };

  // Knyt en lös betalning till vald bokning
  const handleLinkPayment = async (paymentId) => {
    const appointmentId = linkChoice[paymentId];
    if (!appointmentId) return;
    try {
      setLinkingId(paymentId);
      await apiClient.post(`/api/calendar/payments/${paymentId}/link`, { appointment_id: appointmentId });
      // Ta bort ur listan (kopplingen är klar)
      setUnlinkedPayments((prev) => ({
        items: prev.items.filter((p) => p.payment_id !== paymentId),
        total: Math.max(0, prev.total - 1),
      }));
      setLinkChoice((prev) => {
        const next = { ...prev };
        delete next[paymentId];
        return next;
      });
    } catch (err) {
      console.error('Kunde inte koppla betalning:', err);
    } finally {
      setLinkingId(null);
    }
  };

  // Ta bort (makulera) en lös betalning som inte hör någonstans
  const handleDeleteUnlinkedPayment = async (paymentId) => {
    if (!window.confirm('Ta bort denna betalning? Kassaposten makuleras och kan inte ångras.')) return;
    try {
      setLinkingId(paymentId);
      await apiClient.delete(`/api/calendar/payments/${paymentId}`);
      setUnlinkedPayments((prev) => ({
        items: prev.items.filter((p) => p.payment_id !== paymentId),
        total: Math.max(0, prev.total - 1),
      }));
    } catch (err) {
      console.error('Kunde inte ta bort betalning:', err);
    } finally {
      setLinkingId(null);
    }
  };

  const PAYMENT_METHOD_LABELS = { card: 'Kort', cash: 'Kontant', swish: 'Swish', frikort: 'Frikort', uteblivet: 'Uteblivet', avgiftsfri: 'Avgiftsfritt (ej fysiskt besök)' };

  const formatShortDate = (isoString) =>
    new Date(isoString).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });

  const dateLabel = now.toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const dateLabelCapitalized = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1);

  // Kliniker utan intern journalmodul: enkel välkomstvy
  if (!useInternalJournal) {
    return (
      <div className="dashboard">
        <div className="dashboard-header">
          <div>
            <h1>{greetingForHour(now.getHours())}, {user?.first_name}!</h1>
            <p className="dashboard-date">{dateLabelCapitalized}{user?.clinic?.clinic_name ? ` · ${user.clinic.clinic_name}` : ''}</p>
          </div>
        </div>
        <p className="dashboard-empty">Journalmodulen är inte aktiverad för din klinik. Kontakta din administratör.</p>
        <div className="dashboard-board-standalone">
          <BoardCard />
        </div>
      </div>
    );
  }

  const todaysAppointments = appointments.filter(
    (a) => a.start_time && a.start_time.startsWith(todayStr)
  );
  const upcomingGroups = appointments
    .filter((a) => a.is_group && new Date(a.end_time) > now)
    .slice(0, 4);
  const nextAppointment = todaysAppointments.find((a) => new Date(a.end_time) > now);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>{greetingForHour(now.getHours())}, {user?.first_name}!</h1>
          <p className="dashboard-date">{dateLabelCapitalized}{user?.clinic?.clinic_name ? ` · ${user.clinic.clinic_name}` : ''}</p>
        </div>

        <div className="dashboard-search">
          <i className="fas fa-search" aria-hidden="true"></i>
          <input
            type="text"
            placeholder="Sök patient (namn eller personnummer)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchResults.length > 0) {
                openPatient(searchResults[0].patient_id);
              }
              if (e.key === 'Escape') setSearchOpen(false);
            }}
          />
          {searchOpen && (
            <div className="dashboard-search-results">
              {searchResults.length === 0 ? (
                <div className="search-result-empty">Inga träffar</div>
              ) : (
                searchResults.map((p) => (
                  <button
                    key={p.patient_id}
                    className="search-result-item"
                    onMouseDown={() => openPatient(p.patient_id)}
                  >
                    <span className="search-result-name">{p.firstname} {p.lastname}</span>
                    <span className="search-result-pnr">{p.personnummer}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-actions">
        <button className="dashboard-action-btn" onClick={() => setShowQuickPatient(true)}>
          <i className="fas fa-user-plus" aria-hidden="true"></i> Ny patient
        </button>
        <button className="dashboard-action-btn" onClick={() => navigate('/calendar')}>
          <i className="fas fa-calendar-plus" aria-hidden="true"></i> Boka tid
        </button>
        <button className="dashboard-action-btn" onClick={() => navigate('/patients')}>
          <i className="fas fa-users" aria-hidden="true"></i> Patientregister
        </button>
      </div>

      <div className="dashboard-grid">
        {/* Vänster kolumn: dagens bokningar */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2>Dagens bokningar</h2>
            <button className="dashboard-link" onClick={() => navigate('/calendar')}>
              Öppna kalendern <i className="fas fa-arrow-right" aria-hidden="true"></i>
            </button>
          </div>

          {appointmentsLoading ? (
            <p className="dashboard-empty">Laddar…</p>
          ) : todaysAppointments.length === 0 ? (
            <p className="dashboard-empty">Inga bokningar idag.</p>
          ) : (
            <ul className="appointment-list">
              {todaysAppointments.map((a) => {
                const isNext = nextAppointment &&
                  a.appointment_id === nextAppointment.appointment_id &&
                  a.start_time === nextAppointment.start_time;
                const isPast = new Date(a.end_time) < now;
                const chargeable = !a.is_group && a.patient_id;
                const unpaid = isPast && chargeable && !a.is_paid;
                const paid = chargeable && a.is_paid;
                return (
                  <li key={`${a.appointment_id}-${a.start_time}`}>
                    <button
                      className={`appointment-row${isNext ? ' next' : ''}${isPast ? ' past' : ''}`}
                      onClick={() => openAppointment(a)}
                      onContextMenu={(e) => handleRowContextMenu(e, a)}
                    >
                      <span className="appointment-time">{formatTime(a.start_time)}</span>
                      <span className="appointment-info">
                        <span className="appointment-title">
                          {a.is_group ? (
                            <>
                              <i className="fas fa-users appointment-group-icon" aria-hidden="true"></i>
                              {a.group_name || 'Gruppbokning'}
                            </>
                          ) : a.patient ? (
                            `${a.patient.firstname} ${a.patient.lastname}`
                          ) : (
                            'Bokning'
                          )}
                          {isNext && <span className="badge-next">Nästa</span>}
                          {unpaid && <span className="badge-unpaid">Obetald</span>}
                          {paid && <span className="badge-paid">Betald</span>}
                        </span>
                        <span className="appointment-subtitle">
                          {formatTime(a.start_time)}–{formatTime(a.end_time)}
                          {a.appointment_type?.name ? ` · ${a.appointment_type.name}` : ''}
                          {a.is_group && Array.isArray(a.participants)
                            ? ` · ${a.participants.filter((p) => p.status !== 'cancelled').length} deltagare`
                            : ''}
                        </span>
                      </span>
                      <i className="fas fa-chevron-right appointment-chevron" aria-hidden="true"></i>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {/* Högerklicksmeny: journalgenvägar för ett patientbesök */}
          {rowMenu && (
            <div
              className="appointment-context-menu"
              style={{ top: rowMenu.y, left: rowMenu.x }}
            >
              <button
                onClick={() =>
                  navigate(`/patients/${rowMenu.appointment.patient_id}?tab=journal`)
                }
              >
                <i className="fas fa-book-medical"></i> Öppna journal
              </button>
              <button
                onClick={() => {
                  // Virtuellt tillfälle → referens "<serie>@<datum>"; bokningen
                  // skapas först när anteckningen sparas (Spår R)
                  const a = rowMenu.appointment;
                  navigate(`/patients/${a.patient_id}?tab=journal&ny_journal=1&appointment_id=${encodeURIComponent(appointmentKey(a))}`);
                }}
              >
                <i className="fas fa-microphone"></i> Starta journalinspelning
              </button>
            </div>
          )}

          {/* Åtgärdsmeny för en "Att åtgärda"-post */}
          {actionMenu && (
            <div
              className="appointment-context-menu"
              style={{ top: actionMenu.y, left: actionMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => writeJournalForItem(actionMenu.item)}>
                <i className="fas fa-book-medical"></i> Skriv journal
              </button>
              <button onClick={() => openLinkNote(actionMenu.item)}>
                <i className="fas fa-link"></i> Koppla notat
              </button>
              {actionMenu.item.kind === 'forgotten' && (
                <>
                  <button onClick={() => openQuickCash(actionMenu.item)}>
                    <i className="fas fa-cash-register"></i> Kassaregistrera
                  </button>
                  <button onClick={() => markNoShow(actionMenu.item)}>
                    <i className="fas fa-user-slash"></i> Markera uteblivet
                  </button>
                </>
              )}
              <button onClick={() => dismissActionItem(actionMenu.item)}>
                <i className="fas fa-check"></i> Det stämmer – ta bort påminnelsen
              </button>
            </div>
          )}

          {/* Snabbkassa-modal från "Att åtgärda" */}
          {cashModal && (
            <div className="quick-cash-overlay" onClick={() => setCashModal(null)}>
              <div className="quick-cash-modal" onClick={(e) => e.stopPropagation()}>
                <div className="quick-cash-header">
                  <h3>Kassaregistrera</h3>
                  <button className="close-btn" onClick={() => setCashModal(null)}>&times;</button>
                </div>
                <div className="quick-cash-body">
                  <p className="quick-cash-patient">{cashModal.patient?.fullname || 'Okänd patient'}</p>
                  <label>Belopp (kr)</label>
                  <input
                    type="number"
                    value={cashMethod === 'avgiftsfri' ? '0' : cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    disabled={cashMethod === 'avgiftsfri'}
                    min="0"
                    step="0.01"
                    autoFocus
                  />
                  <label>Betalmetod</label>
                  <div className="quick-cash-methods">
                    {[['cash', 'Kontant'], ['card', 'Kort'], ['swish', 'Swish'], ['frikort', 'Frikort'], ['avgiftsfri', 'Avgiftsfritt (ej fysiskt besök)']].map(([v, l]) => (
                      <button
                        key={v}
                        type="button"
                        className={cashMethod === v ? 'active' : ''}
                        onClick={() => setCashMethod(v)}
                      >
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="quick-cash-footer">
                  <button className="btn btn-secondary" onClick={() => setCashModal(null)}>Avbryt</button>
                  <button
                    className="btn btn-primary"
                    disabled={savingCash || (!cashAmount && cashMethod !== 'avgiftsfri')}
                    onClick={submitQuickCash}
                  >
                    {savingCash ? 'Registrerar…' : 'Registrera'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Koppla notat-modal från "Att åtgärda" (Spår A) */}
          {linkModal && (
            <div className="quick-cash-overlay" onClick={() => setLinkModal(null)}>
              <div className="quick-cash-modal" onClick={(e) => e.stopPropagation()}>
                <div className="quick-cash-header">
                  <h3>Koppla notat</h3>
                  <button className="close-btn" onClick={() => setLinkModal(null)}>&times;</button>
                </div>
                <div className="quick-cash-body">
                  <p className="quick-cash-patient">{linkModal.item.patient?.fullname || 'Okänd patient'}</p>
                  {linkEntriesLoading ? (
                    <p className="dashboard-empty">Laddar notat…</p>
                  ) : linkEntries.length === 0 ? (
                    <p className="dashboard-empty">
                      Inga okopplade notat hittades för patienten runt bokningsdagen.
                    </p>
                  ) : (
                    <ul className="link-note-list">
                      {linkEntries.map((e) => (
                        <li key={e.entry_id}>
                          <button
                            type="button"
                            className="link-note-row"
                            disabled={linkingEntryId !== null}
                            onClick={() => linkNoteToItem(e.entry_id)}
                          >
                            <span className="link-note-main">
                              <span className="link-note-title">
                                {e.display_name || e.template_name || 'Journalanteckning'}
                                <span className={`link-note-status ${e.status}`}>
                                  {e.status === 'signed' ? 'signerad' : 'utkast'}
                                </span>
                              </span>
                              {e.preview && <span className="link-note-preview">{e.preview}</span>}
                            </span>
                            <span className="link-note-date">
                              {e.created_at ? new Date(e.created_at).toLocaleDateString('sv-SE') : ''}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="quick-cash-footer">
                  <button className="btn btn-secondary" onClick={() => setLinkModal(null)}>Avbryt</button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Höger kolumn */}
        <div className="dashboard-side">
          {/* Anslagstavla: klinikens delade lappar (Spår A) */}
          <BoardCard />

          {/* Att åtgärda: betalda utan anteckning + bortglömda bokningar */}
          {actionItems.total > 0 && (
            <div className="dashboard-card dashboard-action-items">
              <div className="dashboard-card-header">
                <h2>
                  <i className="fas fa-triangle-exclamation action-items-icon" aria-hidden="true"></i> Att åtgärda
                </h2>
              </div>
              <p className="action-items-summary">
                {actionItems.counts.paid_no_note > 0 && (
                  <>{actionItems.counts.paid_no_note} betald{actionItems.counts.paid_no_note === 1 ? '' : 'a'} utan anteckning</>
                )}
                {actionItems.counts.paid_no_note > 0 && actionItems.counts.forgotten > 0 && ' · '}
                {actionItems.counts.forgotten > 0 && (
                  <>{actionItems.counts.forgotten} ej kassaregistrerad{actionItems.counts.forgotten === 1 ? '' : 'e'}</>
                )}
              </p>
              <ul className="todo-list">
                {actionItems.items.map((item) => (
                  <li key={`${item.appointment_id}-${item.start_time}`}>
                    <button className="todo-row" onClick={(e) => openActionMenu(e, item)}>
                      <span className="action-item-main">
                        <span className="todo-patient">
                          {item.patient?.fullname || 'Okänd patient'}
                          {item.group_name ? ` · Grupp: ${item.group_name}` : ''}
                        </span>
                        <span className={`action-item-tag ${item.kind}`}>
                          {item.kind === 'paid_no_note' ? 'Betald · saknar anteckning' : 'Ej kassaregistrerad · saknar anteckning'}
                        </span>
                      </span>
                      <span className="todo-date">{item.start_time ? formatShortDate(item.start_time) : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Oknutna betalningar: lösa betalningar som kan knytas till en bokning */}
          {unlinkedPayments.total > 0 && (
            <div className="dashboard-card dashboard-action-items">
              <div className="dashboard-card-header">
                <h2>
                  <i className="fas fa-link action-items-icon" aria-hidden="true"></i> Oknutna betalningar
                </h2>
              </div>
              <p className="action-items-summary">
                {unlinkedPayments.total} betalning{unlinkedPayments.total === 1 ? '' : 'ar'} utan kopplad bokning
              </p>
              <ul className="todo-list">
                {unlinkedPayments.items.map((p) => (
                  <li key={p.payment_id} className="unlinked-payment-row">
                    <div className="unlinked-payment-info">
                      <span className="todo-patient">{p.patient?.fullname || 'Okänd patient'}</span>
                      <span className="unlinked-payment-meta">
                        {p.amount} kr · {PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method}
                        {p.payment_date ? ` · ${formatShortDate(p.payment_date)}` : ''}
                      </span>
                    </div>
                    <div className="unlinked-payment-right">
                      {p.candidates && p.candidates.length > 0 ? (
                        <div className="unlinked-payment-actions">
                          <select
                            className="unlinked-payment-select"
                            value={linkChoice[p.payment_id] || ''}
                            onChange={(e) =>
                              setLinkChoice((prev) => ({
                                ...prev,
                                [p.payment_id]: e.target.value ? parseInt(e.target.value, 10) : '',
                              }))
                            }
                          >
                            <option value="">Välj bokning…</option>
                            {p.candidates.map((c) => (
                              <option key={c.appointment_id} value={c.appointment_id}>
                                {c.start_time ? formatShortDate(c.start_time) : ''}
                                {c.appointment_type ? ` · ${c.appointment_type.name}` : ''}
                                {c.is_paid ? ' (redan betald)' : ''}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            disabled={!linkChoice[p.payment_id] || linkingId === p.payment_id}
                            onClick={() => handleLinkPayment(p.payment_id)}
                          >
                            {linkingId === p.payment_id ? 'Kopplar…' : 'Koppla'}
                          </button>
                        </div>
                      ) : (
                        <span className="unlinked-payment-nopatient">
                          {p.patient ? 'Inga bokningar att koppla till' : 'Ingen patient – koppla i kassan'}
                        </span>
                      )}
                      <button
                        type="button"
                        className="unlinked-payment-delete"
                        title="Ta bort betalning"
                        disabled={linkingId === p.payment_id}
                        onClick={() => handleDeleteUnlinkedPayment(p.payment_id)}
                      >
                        <i className="fas fa-trash" aria-hidden="true"></i>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Att göra */}
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h2>Att göra</h2>
            </div>
            {unsigned.total === 0 ? (
              <p className="dashboard-empty">
                <i className="fas fa-check-circle todo-done-icon" aria-hidden="true"></i> Inga osignerade anteckningar
              </p>
            ) : (
              <>
                <p className="todo-summary">
                  <i className="fas fa-file-signature todo-alert-icon" aria-hidden="true"></i>
                  {unsigned.total} osignerad{unsigned.total === 1 ? '' : 'e'} anteckning{unsigned.total === 1 ? '' : 'ar'}
                </p>
                <ul className="todo-list">
                  {unsigned.entries.map((e) => (
                    <li key={e.entry_id}>
                      <button
                        className="todo-row"
                        onClick={() => navigate(`/patients/${e.patient_id}?tab=journal`)}
                      >
                        <span className="todo-patient">{e.patient?.fullname || 'Okänd patient'}</span>
                        <span className="todo-date">
                          {e.created_at ? new Date(e.created_at).toLocaleDateString('sv-SE') : ''}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>

          {/* Kommande grupper */}
          {upcomingGroups.length > 0 && (
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h2>Kommande grupper</h2>
              </div>
              <ul className="todo-list">
                {upcomingGroups.map((g) => (
                  <li key={`${g.appointment_id}-${g.start_time}`}>
                    <button className="todo-row" onClick={() => openAppointment(g)}>
                      <span className="todo-patient">
                        <i className="fas fa-users appointment-group-icon" aria-hidden="true"></i>
                        {g.group_name || 'Gruppbokning'}
                      </span>
                      <span className="todo-date">
                        {new Date(g.start_time).toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'numeric' })} {formatTime(g.start_time)}
                        {Array.isArray(g.participants)
                          ? ` · ${g.participants.filter((p) => p.status !== 'cancelled').length} st`
                          : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Senast öppnade patienter */}
          {recentPatients.length > 0 && (
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h2>Senast öppnade</h2>
              </div>
              <ul className="todo-list">
                {recentPatients.map((p) => (
                  <li key={p.patient_id}>
                    <button className="todo-row" onClick={() => navigate(`/patients/${p.patient_id}`)}>
                      <span className="todo-patient">{p.name}</span>
                      <i className="fas fa-chevron-right appointment-chevron" aria-hidden="true"></i>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Dagskassa - endast admin */}
          {isAdmin && cashTotal != null && (
            <div className="dashboard-card dashboard-cash">
              <button className="todo-row" onClick={() => navigate('/daily-cash-report')}>
                <span className="todo-patient">
                  <i className="fas fa-cash-register" aria-hidden="true"></i> Dagskassa idag
                </span>
                <span className="cash-amount">
                  {Number(cashTotal).toLocaleString('sv-SE')} kr
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      <QuickPatientModal isOpen={showQuickPatient} onClose={() => setShowQuickPatient(false)} />

      {/* Bekräfta att avfärda en "Att åtgärda"-påminnelse (Spår R) */}
      <ConfirmDialog
        isOpen={dismissTarget !== null}
        variant="danger"
        title="Ta bort påminnelse?"
        message={`Påminnelsen${dismissTarget?.patient?.fullname ? ` för ${dismissTarget.patient.fullname}` : ''} döljs permanent från "Att åtgärda".`}
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        loading={dismissing}
        onConfirm={confirmDismiss}
        onCancel={() => setDismissTarget(null)}
      />
    </div>
  );
};

export default Dashboard;
